import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';

import { Button, Card } from '../components/index.ts';
import { db } from '../db/db.ts';
import { syncRepository } from '../db/repositories/sync.ts';
import { syncNow } from './engine.ts';

/**
 * Entries that have failed to reach the server ten times running.
 *
 * They are still on this device and still in the queue — nothing is ever
 * dropped — but ten failures means something is wrong that retrying quietly
 * will not fix, so it is said out loud rather than left to a silent amber dot.
 * Hazard H2 in COMPLIANCE.md is a trainee believing their work is backed up
 * when it is not.
 *
 * Phase 13 builds the full sync status panel; this is the part phase 12 owes.
 */
export function SyncProblems({ userId }: { userId: string | null }) {
  const [retrying, setRetrying] = useState(false);

  const failed = useLiveQuery(async () => {
    if (userId === null) return [];

    const items = await db.outbox.where('status').equals('failed').toArray();
    return items.filter((item) => item.userId === userId);
  }, [userId]);

  if (userId === null || failed === undefined || failed.length === 0) return null;

  async function retry(): Promise<void> {
    if (userId === null) return;

    setRetrying(true);
    try {
      await syncRepository.retryFailed(userId);
      await syncNow(userId);
    } finally {
      setRetrying(false);
    }
  }

  const reason = failed[0]?.lastError ?? 'unknown';

  return (
    <Card title="Sync problems">
      <p className="text-sm text-text">
        {failed.length === 1
          ? 'One entry has not reached the server.'
          : `${String(failed.length)} entries have not reached the server.`}
      </p>
      <p className="pt-1 text-xs text-muted">
        They are safe on this device and nothing has been lost. Last problem: {reason}.
      </p>

      <div className="pt-3">
        <Button variant="secondary" loading={retrying} onClick={() => void retry()}>
          Try again
        </Button>
      </div>
    </Card>
  );
}
